import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUserProfileCreate } from '../../api/hooks/User/useUserProfileCreate'
import { useAuthStore } from '../../store'
import { AvatarColor, AvatarType, useUserStore } from '../../store/useUserStore'
import styles from '../../styles/user/UserSignIn.module.scss'
import { Icon } from '../../assets/icons'
import BaseButton from '../common/BaseButton'
import SignInInput from '../common/SignInInput'

interface UserSignInContentProps {
  avatar?: React.ReactNode
  onSuccess?: () => void
}

const CHARACTERS = [
  { id: 'Buddy', label: 'Buddy', src: '/assets/avatar/characters/Buddy.png' },
  { id: 'Explorer', label: 'Explorer', src: '/assets/avatar/characters/Explorer.png' },
  { id: 'Thinker', label: 'Thinker', src: '/assets/avatar/characters/Thinker.png' },
] as const

type CharacterId = (typeof CHARACTERS)[number]['id']

const UserSignInContent = ({ avatar, onSuccess }: UserSignInContentProps) => {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const { setUserProfile } = useUserStore()
  const { updateUserPartial } = useAuthStore()

  const {
    mutateAsync: createUserProfile,
    isPending,
    error,
    isSuccess,
  } = useUserProfileCreate()

  const [nickname, setNickname] = useState('')
  const [selectedCharacter, setSelectedCharacter] = useState<CharacterId>('Buddy')

  const currentAvatarSrc = `/assets/avatar/characters/${selectedCharacter}.png`

  const handleSubmit = async () => {
    if (!nickname.trim()) {
      alert('닉네임을 입력해주세요.')
      return
    }

    if (!user?.ID) {
      alert('사용자 인증 정보가 없습니다.')
      return
    }

    try {
      const apiRequestData = {
        nickname: nickname.trim(),
        avatarImagePath: currentAvatarSrc,
        ID: user.ID,
      }

      const result = await createUserProfile(apiRequestData)

      updateUserPartial({
        NICKNAME: nickname.trim(),
        AVATAR: currentAvatarSrc,
      })

      setUserProfile({
        nickname: nickname.trim(),
        avatarType: selectedCharacter as unknown as AvatarType,
        avatarColor: 'blue' as AvatarColor,
        avatarImagePath: currentAvatarSrc,
      })

      console.log('User profile created successfully:', result)
    } catch (error) {
      console.error('프로필 생성 실패:', error)
      alert(
        error instanceof Error ? error.message : '프로필 생성에 실패했습니다.'
      )
    }
  }

  useEffect(() => {
    if (isSuccess) {
      onSuccess?.()
      navigate('/solver')
    }
  }, [isSuccess, onSuccess, navigate])

  useEffect(() => {
    if (error) {
      alert(error instanceof Error ? error.message : '오류가 발생했습니다.')
    }
  }, [error])

  return (
    <div className={styles.userWrapper}>
      <div className={styles.userContainer}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Let's get started!</h2>
          <p className={styles.cardSubtitle}>Create your nickname and choose your avatar.</p>
        </div>

        <div className={styles.nicknameSection}>
          <label className={styles.fieldLabel}>Nickname</label>
          <div className={styles.nicknameWrapper}>
            <SignInInput
              placeholder="Enter your nickname"
              icon={<Icon name="user" color="#0D2A4F" size={18} />}
              maxLength={20}
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
            />
            <span className={styles.charCounter}>{nickname.length}/20</span>
          </div>
        </div>

        <div className={styles.characterSection}>
          <label className={styles.fieldLabel}>Choose your character</label>
          <div className={styles.characterGrid}>
          {CHARACTERS.map((char) => (
            <button
              key={char.id}
              type="button"
              className={`${styles.characterCard} ${
                selectedCharacter === char.id ? styles.characterCardSelected : ''
              }`}
              onClick={() => setSelectedCharacter(char.id)}
            >
              {selectedCharacter === char.id && (
                <div className={styles.checkBadge}>✓</div>
              )}
              <img
                src={char.src}
                alt={char.label}
                className={styles.characterImage}
              />
              <span className={styles.characterName}>{char.label}</span>
            </button>
          ))}
          </div>
        </div>

        <div className={styles.startButtonWrapper}>
          <BaseButton
            className={styles.startButton}
            onClick={handleSubmit}
            disabled={!nickname.trim() || isPending}
          >
            {isPending ? 'Loading...' : 'Start'}
          </BaseButton>
        </div>
      </div>
    </div>
  )
}

export default UserSignInContent
